import { createContext, useCallback, useContext, useEffect, useMemo, useState, type ReactNode } from "react"
import { createPortal } from "react-dom"
import { ChevronDown, Cookie, Megaphone, ShieldCheck, Sparkles, X } from "lucide-react"
import { clearStoredConsent, readStoredConsent, storeConsent } from "src/consent/consentStorage"
import posthog from "src/consent/posthog"
import { type ConsentSelection, type CookieConsent } from "src/consent/consentTypes"
import { syncAnalyticsWithConsent } from "src/consent/analytics"
import "./ConsentProvider.css"

interface ConsentContextValue {
    consent: CookieConsent | null
    hasAnalyticsConsent: boolean
    hasMarketingConsent: boolean
    acceptAll: () => void
    rejectNonEssential: () => void
    savePreferences: (selection: ConsentSelection) => void
    openCookieSettings: () => void
}

const ConsentContext = createContext<ConsentContextValue | undefined>(undefined)
const NO_OP_CONSENT: ConsentContextValue = {
    consent: null,
    hasAnalyticsConsent: false,
    hasMarketingConsent: false,
    acceptAll: () => undefined,
    rejectNonEssential: () => undefined,
    savePreferences: () => undefined,
    openCookieSettings: () => undefined,
}

export function useConsent(): ConsentContextValue {
    const context = useContext(ConsentContext)
    return context ?? NO_OP_CONSENT
}

interface ConsentProviderProps {
    children: ReactNode
}

function ConsentBanner({ onAcceptAll, onReject, onManage }: { onAcceptAll: () => void; onReject: () => void; onManage: () => void }) {
    return createPortal(
        <div className="hi-consent-banner" role="dialog" aria-live="polite" aria-label="Cookie consent banner">
            <div className="hi-consent-banner__icon" aria-hidden>
                <Cookie size={20} strokeWidth={1.9} />
            </div>
            <div className="hi-consent-banner__copy">
                <h2>Cookie choices</h2>
                <p>
                    HomeIndex uses necessary cookies to run the site. You can allow analytics and marketing cookies to help us improve the product. We do not sell your data.
                </p>
            </div>
            <div className="hi-consent-banner__actions">
                <button type="button" className="hi-consent-button hi-consent-button--primary" onClick={onAcceptAll}>
                    Accept all
                </button>
                <button type="button" className="hi-consent-button hi-consent-button--ghost" onClick={onReject}>
                    Reject non-essential
                </button>
                <button type="button" className="hi-consent-button hi-consent-button--secondary" onClick={onManage}>
                    Manage preferences
                </button>
            </div>
        </div>,
        document.body
    )
}

function ConsentAccordion({
    title,
    description,
    icon,
    checked,
    onChange,
}: {
    title: string
    description: string
    icon: ReactNode
    checked: boolean
    onChange: (value: boolean) => void
}) {
    const [isOpen, setIsOpen] = useState(false)

    return (
        <details className="hi-consent-accordion" open={isOpen}>
            <summary
                className="hi-consent-accordion__summary"
                onClick={(event) => {
                    event.preventDefault()
                    setIsOpen((value) => !value)
                }}
            >
                <span className="hi-consent-accordion__summary-icon" aria-hidden>
                    {icon}
                </span>
                <span className="hi-consent-accordion__summary-text">
                    <strong>{title}</strong>
                    <span className="hi-consent-accordion__summary-status">{checked ? "On" : "Off"}</span>
                </span>
                <span className="hi-consent-accordion__summary-chevron" aria-hidden>
                    <ChevronDown size={16} strokeWidth={2} />
                </span>
            </summary>
            {isOpen && (
                <div className="hi-consent-accordion__body">
                    <p>{description}</p>
                    <label className="hi-consent-switch">
                        <input type="checkbox" checked={checked} onChange={(event) => onChange(event.target.checked)} />
                        <span className="hi-consent-switch__track" aria-hidden>
                            <span className="hi-consent-switch__thumb" />
                        </span>
                        <span className="hi-consent-switch__label">
                            {checked ? `Disable ${title.toLowerCase()}` : `Enable ${title.toLowerCase()}`}
                        </span>
                    </label>
                </div>
            )}
        </details>
    )
}

function ConsentModal({
    isOpen,
    consent,
    onClose,
    onSave,
}: {
    isOpen: boolean
    consent: CookieConsent | null
    onClose: () => void
    onSave: (selection: ConsentSelection) => void
}) {
    const [analytics, setAnalytics] = useState<boolean>(consent?.analytics ?? false)
    const [marketing, setMarketing] = useState<boolean>(consent?.marketing ?? false)

    useEffect(() => {
        setAnalytics(consent?.analytics ?? false)
        setMarketing(consent?.marketing ?? false)
    }, [consent, isOpen])

    useEffect(() => {
        if (!isOpen) return

        const onKeyDown = (event: KeyboardEvent) => {
            if (event.key === "Escape") {
                onClose()
            }
        }

        window.addEventListener("keydown", onKeyDown)
        document.body.style.overflow = "hidden"
        return () => {
            window.removeEventListener("keydown", onKeyDown)
            document.body.style.overflow = ""
        }
    }, [isOpen, onClose])

    if (!isOpen) {
        return null
    }

    return createPortal(
        <>
            <div className="hi-consent-backdrop" onClick={onClose} />
            <div className="hi-consent-modal" role="dialog" aria-modal="true" aria-labelledby="cookie-settings-title">
                <button className="hi-consent-modal__close" type="button" onClick={onClose} aria-label="Close cookie settings">
                    <X size={16} strokeWidth={2} />
                </button>
                <div className="hi-consent-modal__header">
                    <span className="hi-consent-modal__eyebrow">Privacy</span>
                    <h2 id="cookie-settings-title">Cookie settings</h2>
                    <p>Review each category below. Necessary storage stays on so the site works. We do not sell your data.</p>
                </div>

                <div className="hi-consent-modal__groups">
                    <div className="hi-consent-toggle hi-consent-toggle--locked">
                        <span className="hi-consent-toggle__summary-icon" aria-hidden>
                            <ShieldCheck size={18} strokeWidth={1.9} />
                        </span>
                        <div className="hi-consent-toggle__text">
                            <div className="hi-consent-toggle__title">Necessary</div>
                            <span>Required for login, security, saved state, and core site functionality.</span>
                        </div>
                    </div>

                    <ConsentAccordion
                        title="Analytics"
                        description="PostHog and Google Analytics. Used to understand site usage and improve the product."
                        icon={<Sparkles size={18} strokeWidth={1.9} />}
                        checked={analytics}
                        onChange={setAnalytics}
                    />

                    <ConsentAccordion
                        title="Marketing"
                        description="Google Ads and conversion tracking. Used to measure campaigns and advertising performance."
                        icon={<Megaphone size={18} strokeWidth={1.9} />}
                        checked={marketing}
                        onChange={setMarketing}
                    />
                </div>

                <div className="hi-consent-modal__actions">
                    <button
                        type="button"
                        className="hi-consent-button hi-consent-button--primary"
                        onClick={() => onSave({ analytics, marketing })}
                    >
                        Save preferences
                    </button>
                </div>
            </div>
        </>,
        document.body
    )
}

type ConsentChoiceAction = "accept_all" | "reject_non_essential" | "save_preferences"

function logConsentChoice(action: ConsentChoiceAction, selection: ConsentSelection) {
    // TODO(beta): replace this temporary consent-choice logging with the final
    // privacy flow and remove the beta-only tracking once cookie enforcement ships.
    posthog.capture("cookie_consent_button_clicked", {
        button: action,
        analytics: selection.analytics,
        marketing: selection.marketing,
        updatedAt: new Date().toISOString(),
    })
}

/** The reports pages stay clear of the banner, so the tour reads on its own. The provider sits outside the router, so the path is watched. */
function useOnReportsPage() {
    const check = () => /^\/reports(\/|$)/.test(window.location.pathname)
    const [on, setOn] = useState(check)
    useEffect(() => {
        const timer = window.setInterval(() => setOn(check()), 300)
        return () => window.clearInterval(timer)
    }, [])
    return on
}

export default function ConsentProvider({ children }: ConsentProviderProps) {
    const onReports = useOnReportsPage()
    const initialConsent = useMemo(() => readStoredConsent(), [])
    const [consent, setConsent] = useState<CookieConsent | null>(() => initialConsent)
    const [isBannerVisible, setIsBannerVisible] = useState<boolean>(() => !initialConsent)
    const [isModalOpen, setIsModalOpen] = useState(false)

    useEffect(() => {
        syncAnalyticsWithConsent(consent)
    }, [consent])

    useEffect(() => {
        if (!consent) {
            clearStoredConsent()
        }
    }, [consent])

    useEffect(() => {
        const onOpenCookieSettings = () => {
            if (!consent) {
                setIsBannerVisible(true)
            }
            setIsModalOpen(true)
        }

        window.addEventListener("homeindex:open-cookie-settings", onOpenCookieSettings)
        return () => {
            window.removeEventListener("homeindex:open-cookie-settings", onOpenCookieSettings)
        }
    }, [consent])

    const persistConsent = useCallback((selection: ConsentSelection, action: ConsentChoiceAction) => {
        logConsentChoice(action, selection)
        const nextConsent = storeConsent(selection)
        setConsent(nextConsent)
        setIsBannerVisible(false)
        setIsModalOpen(false)
        syncAnalyticsWithConsent(nextConsent)
    }, [])

    const acceptAll = useCallback(() => {
        persistConsent({ analytics: true, marketing: true }, "accept_all")
    }, [persistConsent])

    const rejectNonEssential = useCallback(() => {
        persistConsent({ analytics: false, marketing: false }, "reject_non_essential")
    }, [persistConsent])

    const savePreferences = useCallback((selection: ConsentSelection) => {
        persistConsent(selection, "save_preferences")
    }, [persistConsent])

    const openCookieSettings = useCallback(() => {
        if (!consent) {
            setIsBannerVisible(true)
        }
        setIsModalOpen(true)
    }, [consent])

    const closeCookieSettings = useCallback(() => {
        setIsModalOpen(false)
    }, [])

    const value = useMemo<ConsentContextValue>(() => ({
        consent,
        hasAnalyticsConsent: !!consent?.analytics,
        hasMarketingConsent: !!consent?.marketing,
        acceptAll,
        rejectNonEssential,
        savePreferences,
        openCookieSettings,
    }), [acceptAll, consent, openCookieSettings, rejectNonEssential, savePreferences])

    return (
        <ConsentContext.Provider value={value}>
            {children}
            {!consent && isBannerVisible && !onReports && (
                <ConsentBanner
                    onAcceptAll={acceptAll}
                    onReject={rejectNonEssential}
                    onManage={() => setIsModalOpen(true)}
                />
            )}
            <ConsentModal
                isOpen={isModalOpen}
                consent={consent}
                onClose={closeCookieSettings}
                onSave={savePreferences}
            />
        </ConsentContext.Provider>
    )
}
